import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, ProgressBar, cn, formatCurrency, costLineTypeKey } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';

type CostType = { line_type: string; currency: string; total_cost: number };
type CostProp = { property_name: string; currency: string; total_cost: number };
type CostAsset = { asset_name: string; currency: string; total_cost: number };
type SlRow = { service_line_code: string; total: number; overdue: number; compliance_pct: number | null };
type SvcLine = { code: string; name_i18n: Record<string, string> };

export function Reports() {
  const { t, i18n } = useTranslation();
  const [byType, setByType] = useState<CostType[]>([]);
  const [byProp, setByProp] = useState<CostProp[]>([]);
  const [byAsset, setByAsset] = useState<CostAsset[]>([]);
  const [bySl, setBySl] = useState<SlRow[]>([]);
  const [svcLines, setSvcLines] = useState<SvcLine[]>([]);

  useEffect(() => {
    (async () => {
      const [a, b, c, d, sl] = await Promise.all([
        supabase.from('dash_costs_by_type').select('*'),
        supabase.from('dash_costs_by_property').select('*').order('total_cost', { ascending: false }),
        supabase.from('dash_costs_by_asset').select('*').order('total_cost', { ascending: false }).limit(10),
        supabase.from('dash_compliance_service_line').select('*').order('compliance_pct', { ascending: true }),
        supabase.from('service_lines').select('code,name_i18n').is('deleted_at', null),
      ]);
      setByType((a.data as CostType[]) ?? []);
      setByProp((b.data as CostProp[]) ?? []);
      setByAsset((c.data as CostAsset[]) ?? []);
      setBySl((d.data as SlRow[]) ?? []);
      setSvcLines((sl.data as SvcLine[]) ?? []);
    })();
  }, []);

  const total = useMemo(() => byType.reduce((s, r) => s + Number(r.total_cost || 0), 0), [byType]);
  const currency = byType[0]?.currency ?? byProp[0]?.currency ?? '';
  const slName = (code: string) => {
    const n = svcLines.find((s) => s.code === code)?.name_i18n;
    return n?.[i18n.language] ?? n?.en ?? code;
  };

  return (
    <Layout header={<Topbar />}>
      <div className="flex flex-col gap-[18px] px-7 py-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.reports')}</h1>
          <span className="font-mono text-sm text-muted">{t('cost.total')}: {formatCurrency(total, currency, i18n.language)}</span>
        </div>

        <div className="flex flex-wrap items-start gap-[18px]">
          <Card className="min-w-0 flex-[1_1_300px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('dash.costsByType')}</h2>
            {byType.length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {byType.map((r) => (
              <div key={r.line_type} className="flex items-center justify-between border-b border-divider py-2 text-sm last:border-0">
                <span>{t(costLineTypeKey(r.line_type))}</span>
                <span className="font-mono">{formatCurrency(Number(r.total_cost), r.currency, i18n.language)}</span>
              </div>
            ))}
          </Card>

          <Card className="min-w-0 flex-[1_1_300px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('dash.costsByProperty')}</h2>
            {byProp.length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {byProp.map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-divider py-2 text-sm last:border-0">
                <span>{r.property_name}</span><span className="font-mono">{formatCurrency(Number(r.total_cost), r.currency, i18n.language)}</span>
              </div>
            ))}
          </Card>

          <Card className="min-w-0 flex-[1_1_300px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('dash.costsByAsset')}</h2>
            {byAsset.length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {byAsset.map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-divider py-2 text-sm last:border-0">
                <span className="truncate">{r.asset_name}</span><span className="font-mono">{formatCurrency(Number(r.total_cost), r.currency, i18n.language)}</span>
              </div>
            ))}
          </Card>
        </div>

        <h2 className="eyebrow">{t('dash.byServiceLine')}</h2>
        <Card className="p-5">
          <div className="flex flex-col gap-3">
            {bySl.length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {bySl.map((r) => (
              <div key={r.service_line_code} className="flex flex-col gap-1">
                <div className="flex items-center justify-between text-sm">
                  <span>{slName(r.service_line_code)}</span>
                  <span className={cn('font-mono', (r.compliance_pct ?? 100) < 80 && 'text-st-overdue-fg')}>{r.compliance_pct ?? '—'}% · {r.overdue}/{r.total}</span>
                </div>
                <ProgressBar value={r.compliance_pct ?? 0} />
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
